import { useEffect, useMemo, useState } from "react";
import useTitle from "../../shared/hooks/useTitle";
import Modal from "../../shared/components/Modal";
import MODAL_NAMES from "../dialogs/ModalName";
import { observer } from "mobx-react-lite";
import { useAppContext } from "../../shared/functions/Context";
import { useNavigate, useParams } from "react-router-dom";
import ObjectiveDepartmentModal from "../dialogs/objective-department/ObjectiveDepartmentModal";
import useBackButton from "../../shared/hooks/useBack";
import ErrorBoundary from "../../shared/components/error-boundary/ErrorBoundary";
import { SCORECARD_TAB, QUARTER1_TAB, QUARTER2_TAB, QUARTER3_TAB, QUARTER4_TAB, } from "../../shared/interfaces/IReviewCycleTabs";
import { IReviewCycleStatus, IReviewCycleType } from "../../shared/models/ScorecardBatch";
import DepartmentScorecardDraftCycle from "./DepartmentScorecardDraftCycle";
import useDepartmentScorecardMetadata from "../../shared/hooks/useDepartmentScorecardMetadata";
import { IScorecardMetadata } from "../../shared/models/ScorecardMetadata";
import DepartmentScorecardQ1Cycle from "./DepartmentScorecardQ1Cycle";
import { LoadingEllipsis } from "../../shared/components/loading/Loading";
import { exportDepartmentExcelScorecard } from "../shared/functions/Excel";
import { generateDepartmentPerformanceAgreementPDF } from "../../shared/functions/scorecard-pdf/GeneratePerformaneAgreementPDF";
import { USER_ROLES } from "../../shared/functions/CONSTANTS";
import DepartmentScorecardQ2Cycle from "./DepartmentScorecardQ2Cycle";
import DepartmentScorecardQ3Cycle from "./DepartmentScorecardQ3Cycle";
import DepartmentScorecardQ4Cycle from "./DepartmentScorecardQ4Cycle";
import "./DepartmentScorecard.scss";
import ReadDepartmentScorecardCommentModal from "../dialogs/read-department-scorecard-comment/ReadDepartmentScorecardCommentModal";
import showModalFromId from "../../shared/functions/ModalShow";
import StrategicMapObjectiveDepartmentModal from "../dialogs/strategic-map-objective-department/StrategicMapObjectiveDepartmentModal";
import useVM from "../../shared/hooks/useVM";

interface IStepStageProps {
  open?: boolean;
  status?: IReviewCycleStatus;
  index: number;
  title: IReviewCycleType;
  setReviewCycle: React.Dispatch<React.SetStateAction<IReviewCycleType>>;
}
const StepStage = (props: IStepStageProps) => {
  const status = props.status || "pending";
  const open = props.open ? "open" : "closed";
  const className = `step--stage step--stage__${status} step--stage__${open}`;

  return (
    <ErrorBoundary>
      <button
        className={className}
        onClick={() => props.setReviewCycle(props.title)}
      >
        <div className="step--stage__bubble">
          <div className="step--stage__bubble__content">
            {status === "pending" && (
              <span className="icon" style={{ fontSize: "1.2rem" }}>
                ◔
              </span>
            )}
            {status === "in-progress" && (
              <span className="icon" style={{ fontSize: "1.2rem" }}></span>
            )}
            {status === "submitted" && (
              <span className="icon" style={{ fontSize: "1.2rem" }}>
                ◔
              </span>
            )}
            {(status === "reverted" || status === "cancelled") && (
              <span className="icon" style={{ fontSize: "1.2rem" }}>
                ×
              </span>
            )}
            {status === "approved" && (
              <span className="icon" data-uk-icon="check"></span>
            )}
          </div>
        </div>

        <div className="step--stage__content">
          <p className="label">
            STEP {props.index} {status}
          </p>
          <h6 className="title">{props.title}</h6>
        </div>
      </button>
    </ErrorBoundary>
  );
};

interface IReviewStepProps {
  agreement: IScorecardMetadata;
  reviewCycle: IReviewCycleType;
  setReviewCycle: React.Dispatch<React.SetStateAction<IReviewCycleType>>;
}
const ReviewCycleTabs = observer((props: IReviewStepProps) => {
  const { agreement, reviewCycle, setReviewCycle } = props;

  const {
    agreementDraft,
    quarter1Review,
    quarter2Review,
    quarter3Review,
    quarter4Review,
  } = agreement;

  const cycle: IReviewCycleType = useMemo(() => {
    const ads = agreementDraft.status;
    const q1r = quarter1Review.status;
    const q2r = quarter2Review.status;
    const q3r = quarter3Review.status;

    // Scorecard
    if (ads === "pending" || ads === "submitted") return "Scorecard";
    else if (
      ads === "approved" &&
      (q1r === "pending" || q1r === "in-progress" || q1r === "submitted")
    )
      return "Q1 Reviews";
    else if (
      q1r === "approved" &&
      (q2r === "pending" || q2r === "in-progress" || q2r === "submitted")
    )
      return "Midterm Reviews";
    else if (
      q2r === "approved" &&
      (q3r === "pending" || q3r === "in-progress" || q3r === "submitted")
    )
      return "Q3 Reviews";
    else if (q3r === "approved") return "Assessment";
    else return "Scorecard";
  }, [
    agreementDraft.status,
    quarter1Review.status,
    quarter2Review.status,
    quarter3Review.status,
  ]);

  useEffect(() => {
    setReviewCycle(cycle);
  }, [cycle, setReviewCycle]);

  return (
    <div className="step">
      <StepStage
        index={1}
        title={SCORECARD_TAB.name}
        status={agreementDraft.status}
        open={reviewCycle === SCORECARD_TAB.name}
        setReviewCycle={setReviewCycle}
      />
      <StepStage
        index={2}
        title={QUARTER1_TAB.name}
        status={quarter1Review.status}
        open={reviewCycle === QUARTER1_TAB.name}
        setReviewCycle={setReviewCycle}
      />
      <StepStage
        index={3}
        title={QUARTER2_TAB.name}
        status={quarter2Review.status}
        open={reviewCycle === QUARTER2_TAB.name}
        setReviewCycle={setReviewCycle}
      />
      <StepStage
        index={4}
        title={QUARTER3_TAB.name}
        status={quarter3Review.status}
        open={reviewCycle === QUARTER3_TAB.name}
        setReviewCycle={setReviewCycle}
      />
      <StepStage
        index={5}
        title={QUARTER4_TAB.name}
        status={quarter4Review.status}
        open={reviewCycle === QUARTER4_TAB.name}
        setReviewCycle={setReviewCycle}
      />
    </div>
  );
});

const DepartmentScorecard = observer(() => {
  const { api, store, ui } = useAppContext();
  const { fyid = 'defaultFyid', departmentId = 'defaultDepartmentId' } = useParams<{ fyid?: string, departmentId?: string }>();
  const { vision, mission } = useVM();
  const scorecard = store.scorecard.getById(`${fyid}`); // scorecard.
  const agreement = useDepartmentScorecardMetadata(`${departmentId}`);

  const objectives = store.departmentObjective.getByDepartment(departmentId);

  const role = store.auth.role;
  const meDepartmentID = store.auth.department; // logged-in user's department

  const hasAccess = useMemo(() => {
    return (role === USER_ROLES.SUPER_USER || (role === USER_ROLES.EXECUTIVE_USER && departmentId === meDepartmentID));
  }, [departmentId, meDepartmentID, role]);

  const [title, setTitle] = useTitle(); // set page title
  const [cycle, setCycle] = useState<IReviewCycleType>(SCORECARD_TAB.name);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  useBackButton("/c/strategy/department/");

  // Export reports
  const handleExportPDF = async () => {
    if (!departmentId) return;

    const strategicObjectives = store.companyObjective.all.map((o) => o.asJson) || [];
    const contributoryObjectives = store.departmentObjective.getByDepartment(departmentId).map((o) => o.asJson) || [];
    const measures = store.departmentMeasure.getByDepartment(departmentId).map((o) => o.asJson) || [];

    try {
      generateDepartmentPerformanceAgreementPDF(
        title,
        vision,
        mission,
        strategicObjectives,
        contributoryObjectives,
        measures
      );
    } catch (error) {
      ui.snackbar.load({
        id: Date.now(),
        message:
          "Error! Failed to export.",
        type: "danger",
      });
    }
  };

  const handleExportExcel = async () => {
    if (!departmentId) return;

    const strategicObjectives = store.companyObjective.all.map((o) => o.asJson) || [];
    const contributoryObjectives = store.departmentObjective.getByDepartment(departmentId).map((o) => o.asJson) || [];
    const measures = store.departmentMeasure.getByDepartment(departmentId).map((o) => o.asJson) || [];
    try {
      await exportDepartmentExcelScorecard(
        title,
        strategicObjectives,
        contributoryObjectives,
        measures
      );
    } catch (error) {
      ui.snackbar.load({
        id: Date.now(),
        message:
          "Error! Failed to export.",
        type: "danger",
      });
      console.log(error);

    }
  };

  const handleFeedback = () => {
    showModalFromId(MODAL_NAMES.EXECUTION.READ_DEPARTMENT_SCORECARD_COMMENT_MODAL);
  };

  useEffect(() => {
    const setPageTitle = () => {
      const department = store.department.getById(`${departmentId}`);
      const scorecardName = scorecard ? scorecard.asJson.description : "";
      const departmentName = department ? department.asJson.name : "";

      if (scorecard) setTitle(`${departmentName} ${scorecardName} Scorecard`);
      else navigate("/c/strategy/department/");
    };

    setPageTitle();
  }, [departmentId, navigate, scorecard, setTitle, store.department]);

  useEffect(() => {
    // load department objectives from db
    const loadAll = async () => {
      if (!fyid || !departmentId) return;

      setLoading(true); // start loading
      try {
        // Only get when exporting and drafting.
        await api.companyMeasure.getAll(fyid);
        await api.companyObjective.getAll(fyid);

        // Get all on-load.
        await api.departmentMeasure.getAll(fyid, departmentId);
        await api.departmentObjective.getAll(fyid, departmentId);
        await api.strategicTheme.getAll(fyid);//
      } catch (error) {
        console.log(error);
      }
      setLoading(false); // stop loading
    };

    loadAll();
  }, [
    api.companyMeasure,
    api.companyObjective,
    api.departmentMeasure,
    api.departmentObjective,
    departmentId,
    fyid,
    api.strategicTheme
  ]);

  if (!scorecard) return <></>;

  return (
    <ErrorBoundary>
      <ErrorBoundary>
        <div className="scorecard-page">
          <ReviewCycleTabs
            agreement={agreement}
            reviewCycle={cycle}
            setReviewCycle={setCycle}
          />
        </div>
      </ErrorBoundary>

      {!loading && (
        <ErrorBoundary>
          {cycle === SCORECARD_TAB.name && (
            <DepartmentScorecardDraftCycle
              departmentId={departmentId}
              agreement={agreement}
              objectives={objectives}
              hasAccess={hasAccess}
              handleExportExcel={handleExportExcel}
              handleExportPDF={handleExportPDF}
              handleFeedback={handleFeedback}
            />
          )}
          {cycle === QUARTER1_TAB.name && (
            <DepartmentScorecardQ1Cycle
              departmentId={departmentId}
              agreement={agreement}
              objectives={objectives}
              hasAccess={hasAccess}
              handleExportExcel={handleExportExcel}
              handleExportPDF={handleExportPDF}
              handleFeedback={handleFeedback}
            />
          )}
          {cycle === QUARTER2_TAB.name && (
            <DepartmentScorecardQ2Cycle
              departmentId={departmentId}
              agreement={agreement}
              objectives={objectives}
              hasAccess={hasAccess}
              handleExportExcel={handleExportExcel}
              handleExportPDF={handleExportPDF}
              handleFeedback={handleFeedback}
            />
          )}
          {cycle === QUARTER3_TAB.name && (
            <DepartmentScorecardQ3Cycle
              departmentId={departmentId}
              agreement={agreement}
              objectives={objectives}
              hasAccess={hasAccess}
              handleExportExcel={handleExportExcel}
              handleExportPDF={handleExportPDF}
              handleFeedback={handleFeedback}
            />
          )}
          {cycle === QUARTER4_TAB.name && (
            <DepartmentScorecardQ4Cycle
              departmentId={departmentId}
              agreement={agreement}
              objectives={objectives}
              hasAccess={hasAccess}
              handleExportExcel={handleExportExcel}
              handleExportPDF={handleExportPDF}
              handleFeedback={handleFeedback}
            />
          )}
        </ErrorBoundary>
      )}

      {/* Loading */}
      <ErrorBoundary>{loading && <LoadingEllipsis />}</ErrorBoundary>

      {/* Modals */}
      <ErrorBoundary>
        <Modal
          modalId={
            MODAL_NAMES.EXECUTION.READ_DEPARTMENT_SCORECARD_COMMENT_MODAL
          }
        >
          <ReadDepartmentScorecardCommentModal agreement={agreement} />
        </Modal>
        <Modal modalId={MODAL_NAMES.EXECUTION.DEPARTMENT_OBJECTIVE_MODAL}>
          <ObjectiveDepartmentModal />
        </Modal>
        <Modal
          modalId={MODAL_NAMES.EXECUTION.DEPARTMENT_MAP_OVERVIEW_MODAL}
          cssClass="uk-modal-container"
        >
          <StrategicMapObjectiveDepartmentModal />
        </Modal>
      </ErrorBoundary>
    </ErrorBoundary>
  );
});

export default DepartmentScorecard;
